import React, { useRef, useState, useEffect } from "react";
import { useGLTF } from "@react-three/drei";
import {Color} from "three";
import { bodyMap } from "./BodyMap";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Html } from "@react-three/drei"


export function Model({
  showFront,
  setModalShow,
  selectedBodyParts,
  setSelectedBodyParts,
  readOnly = false,
  preSelectedParts = []
}) {
  const { nodes, materials } = useGLTF("/body.glb");
  const baseColor = "#38bdf8";
  const hoverColor = "#9a1a1a"

  // Apply highlight color to preSelectedParts on mount
  useEffect(() => {
    if (readOnly && preSelectedParts.length > 0) {
      preSelectedParts.forEach(materialName => {
        if (materials[materialName]) {
          materials[materialName].color = new Color(hoverColor);
        }
      });
    }
  }, [readOnly, preSelectedParts, materials, hoverColor]);

  // Sync material colors with selectedBodyParts (for when parts are removed externally)
  useEffect(() => {
    if (readOnly) return;
    Object.keys(materials).forEach(materialName => {
      if (materials[materialName]) {
        if (selectedBodyParts.includes(materialName)) {
          materials[materialName].color = new Color(hoverColor);
        } else {
          materials[materialName].color = new Color(baseColor);
        }
      }
    });
  }, [selectedBodyParts, materials, readOnly, hoverColor, baseColor]);

  const onHover = (e) => {
    if (readOnly) return;
    e.stopPropagation();
    document.body.style.cursor = 'pointer';
    e.object.material.color = new Color(hoverColor);
    if(!selectedBodyParts.includes(e.object.material.name)) {
      e.object.material.color = new Color(hoverColor);
    }
  }

  const onLeave = (e) => {
    if (readOnly) return;
    e.stopPropagation();
    document.body.style.cursor = 'auto';
    if(!selectedBodyParts.includes(e.object.material.name)) {
      e.object.material.color = new Color(baseColor);
    }
  }

  const handleClick = (e) =>{
    if (readOnly) return;
    e.stopPropagation();
    if(!selectedBodyParts.includes(e.object.material.name) && selectedBodyParts.length < 4) {
      e.object.material.color = new Color(hoverColor);
      setSelectedBodyParts([...selectedBodyParts, e.object.material.name]);
    }
    else if (selectedBodyParts.includes(e.object.material.name) ) {
      e.object.material.color = new Color(baseColor);
      setSelectedBodyParts(selectedBodyParts.filter( x => x != e.object.material.name));
    }

  }


  return (
  <group dispose={null} rotation={showFront ? [Math.PI / 2, 0 , 0] : [-Math.PI / 2, Math.PI , 0]} onPointerOver={(e) => {onHover(e)}} onPointerLeave={(e) => {onLeave(e)}} onClick={(e) => handleClick(e)}>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.BaseMesh_Low_Res_Detailed_Male_Female.geometry}
        material={materials.Main}
        
        scale={0.01}
        material-color={baseColor}
              />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Head.geometry}
        material={materials.HeadMaterial}
        
        scale={0.01}
        material-color={baseColor}
        
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Neck.geometry}
        material={materials.NeckMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightShoulder.geometry}
        material={materials.RightShoulderMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftShoulder.geometry}
        material={materials.LeftShoulderMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Chest.geometry}
        material={materials.ChestMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Core.geometry}
        material={materials.CoreMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightRibs.geometry}
        material={materials.RightRibsMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftRibs.geometry}
        material={materials.LeftRibsMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <group  scale={0.01}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.UpperBack_1.geometry}
          material={materials.UpperBackMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.UpperBack_2.geometry}
          material={materials.Main}
          material-color={baseColor}
        />
      </group>
      <group  scale={0.01}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightMidBack_1.geometry}
          material={materials.RightMidBackMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightMidBack_2.geometry}
          material={materials.RightMidBackMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightMidBack_3.geometry}
          material={materials.RightMidBackMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightMidBack_4.geometry}
          material={materials.RightMidBackMaterial}
          material-color={baseColor}
        />
      </group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftMidBack.geometry}
        material={materials.LeftMidBackMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LowerBack.geometry}
        material={materials.LowerBackMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <group  scale={0.01}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightBicep_1.geometry}
          material={materials.RightBicepMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightBicep_2.geometry}
          material={materials.RightBicepMaterial}
          material-color={baseColor}
        />
      </group>
      <group  scale={0.01}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightTricep_1.geometry}
          material={materials.RightTricepMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.RightTricep_2.geometry}
          material={materials.RightTricepMaterial}
          material-color={baseColor}
        />
      </group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightElbow.geometry}
        material={materials.RightElbowMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightForearm.geometry}
        material={materials.RightForearmMaterial}
        
        material-color={baseColor}
        scale={0.01}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightHand.geometry}
        material={materials.RightHandMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <group  scale={0.01}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.LeftBicep_1.geometry}
          material={materials.LeftBicepMaterial}
          material-color={baseColor}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.LeftBicep_2.geometry}
          material={materials.RightBicepMaterial}
          material-color={baseColor}
        />
      </group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftTricep.geometry}
        material={materials.LeftTricepMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftElbow.geometry}
        material={materials.LeftElbowMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftForeArm.geometry}
        material={materials.LeftForeArm}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftHand.geometry}
        material={materials.LeftHandMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightHip.geometry}
        material={materials.RightHipMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftHip.geometry}
        material={materials.LeftHipMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Pelvis.geometry}
        material={materials.PelvisMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Butt.geometry}
        material={materials.ButtMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightThigh.geometry}
        material={materials.RightThighMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightHamstring.geometry}
        material={materials.RightHamstringMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightKnee.geometry}
        material={materials.RightKneeMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightCalf.geometry}
        material={materials.RightCalfMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightLeg.geometry}
        material={materials.RightLegMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightAnkle.geometry}
        material={materials.RightAnkleMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.RightFoot.geometry}
        material={materials.RightFootMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftThigh.geometry}
        material={materials.LeftThighMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftHamstring.geometry}
        material={materials.LeftHamstringMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftKnee.geometry}
        material={materials.LeftKneeMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftCalf.geometry}
        material={materials.LeftCalfMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftLeg.geometry}
        material={materials.LeftLegMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftAnkle.geometry}
        material={materials.LeftAnkleMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.LeftFoot.geometry}
        material={materials.LeftFootMaterial}
        
        scale={0.01}
        material-color={baseColor}
      />
    </group>
  );
}

useGLTF.preload("/body.glb");